import { useCallback, useEffect, useState } from "react";

import { useLazyViewSurveyViewQuery } from "@/redux/service";
import { downloadPollingService } from "@/shared/services/downloadPollingService";
import { UseSurveyViewViewerOptions } from "@/domains/survey-views/types";

export const useSurveyViewViewer = (
  surveyViewId: string | undefined,
  options: UseSurveyViewViewerOptions = {},
) => {
  const [trigger] = useLazyViewSurveyViewQuery();
  const { pollInterval = 2000, timeout = 300000 } = options;

  const [htmlData, setHtmlData] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const view = useCallback(
    async (id: string) => {
      setIsLoading(true);
      setHasError(false);
      setHtmlData("");

      await downloadPollingService.initiateDownload(
        id,
        trigger,
        { pollInterval, timeout, autoDownload: false },
        {
          onSuccess: async (response) => {
            try {
              const res = await fetch(response.link);
              const html = await res.text();
              setHtmlData(html);
            } catch {
              setHasError(true);
            } finally {
              setIsLoading(false);
            }
          },
          onError: () => {
            setIsLoading(false);
            setHasError(true);
          },
          onTimeout: () => {
            setIsLoading(false);
            setHasError(true);
          },
          onCleanup: () => {},
        },
      );
    },
    [trigger, pollInterval, timeout],
  );

  useEffect(() => {
    if (!surveyViewId) return;
    view(surveyViewId);
    return () => downloadPollingService.stopPolling(surveyViewId);
  }, [surveyViewId, view]);

  return { htmlData, isLoading, hasError };
};
